<template>
  <section
    :class="bem()"
  >
    <TitleBlock
      v-if="block.title?.text"
      :title="block.title"
      :class="bem('title')"
    />
    <div :class="bem('wrapper')">
      <ul
        v-if="formattedItems.length"
        :class="bem('items')"
      >
        <li
          v-for="item in formattedItems"
          :key="item.id"
          :class="
            bem('item', {
              aspect: item.aspectRatio,
              hasMobileImage: item.mobileImage
            })
          "
        >
          <component
            :is="item.link ? VisionLink : 'div'"
            :href="buildInternalLink(item.link)"
            @click="handleClick(item.tracking)"
          >
            <div :class="bem('image', { desktop: true })">
              <DatoCmsImage
                v-if="item.image"
                :data="item.image.responsiveImage"
                layout="fill"
                object-fit="cover"
                :object-position="`${item.image.focalPoint.x * 100}% ${
                  item.image.focalPoint.y * 100
                }%`"
                sizes="(max-width: 600px) 50vw, 300px"
                :priority="!block.lazyLoadImages"
              />
            </div>
            <div :class="bem('image', { mobile: true })">
              <DatoCmsImage
                v-if="item.mobileImage"
                :data="item.mobileImage.responsiveImage"
                layout="fill"
                object-fit="cover"
                :object-position="`${item.mobileImage.focalPoint.x * 100}% ${
                  item.mobileImage.focalPoint.y * 100
                }%`"
                sizes="(max-width: 600px) 50vw, 300px"
                :priority="!block.lazyLoadImages"
              />
            </div>
            <div
              v-if="
                item.backgroundVideo &&
                  item.backgroundVideo.video?.video?.muxPlaybackId
              "
              :class="bem('bg-video')"
            >
              <BaseVideo
                :video="item.backgroundVideo.video"
                :preview-start-time="0"
                :loop="true"
                :bg-video="true"
                :full-width="false"
              />
            </div>
            <div
              v-if="item.backgroundMediaOverlay > 0"
              :style="{ opacity: `${item.backgroundMediaOverlay}` }"
              :class="bem('bg-overlay')"
            />
            <TitleBlock
              v-if="item.text?.text"
              :title="item.text"
              :class="bem('item-title')"
            />
          </component>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup>
import { Image as DatoCmsImage } from 'vue-datocms'
import { VisionLink } from '#components'

const bem = useBEM('GridGallery')
const { buildInternalLink } = useUrlBuilder()
const { $track } = useNuxtApp()

const props = defineProps({
  block: {
    type: Object,
    required: true,
  },
})

const formattedItems = computed(() => {
  return props.block.items.map((item, index) => {
    return {
      id: item.id,
      text: item.text,
      image: item.image,
      mobileImage: item.mobileImage,
      aspectRatio: item.aspectRatio,
      link: item.link,
      backgroundVideo: item.backgroundVideo,
      backgroundMediaOverlay: item.backgroundMediaOverlay / 100,
      tracking: {
        linkText: item.text?.text,
        position: index + 1,
        interactionElement: item.trackingId,
        interactionSection: props.block.trackingId,
        interactionCategory: 'Grid Gallery'
      }
    }
  })
})

async function handleClick(trackingParams) {
  await $track.global.linkClicked(trackingParams, 'Clicked Grid Gallery Item')
}
</script>

<style lang="postcss">
.GridGallery {
  @apply w-full global-wrapper;
}

.GridGallery__title {
  @apply mb-6;
}

.GridGallery__items {
  @apply flex gap-y-5 md:gap-xxs mdMax:flex-wrap;
}

.GridGallery__item {
  @apply relative w-1/6 rounded-md overflow-hidden flex-grow;

  @screen xxlMax {
    &:nth-child(n + 6) {
      @apply hidden;
    }
  }

  @screen lgMax {
    &:nth-child(n + 5) {
      @apply hidden;
    }
  }

  @screen mdMax {
    @apply flex-wrap flex-grow-0;

    &:nth-child(n + 5),
    &:nth-child(n + 6) {
      @apply block;
    }

    &:first-child,
    &:nth-child(n + 2):nth-child(odd) {
      @apply mr-3;
    }

    &:nth-child(1),
    &:nth-child(4),
    &:nth-child(5) {
      width: calc(45% - 6px);
      aspect-ratio: 16/11;
    }

    &:nth-child(2),
    &:nth-child(3),
    &:nth-child(6) {
      width: calc(55% - 6px);
      aspect-ratio: 1/1;
    }
  }
}

.GridGallery__item-title {
  @apply absolute text-white bottom-0 p-3;
}

.GridGallery__bg-video {
  @apply absolute z-0 top-0 left-0 w-full h-full overflow-hidden;
}

.GridGallery__bg-overlay {
  @apply absolute bg-off-black w-full h-full top-0 left-0;
}

/**

MODIFIERS

--aspect-{square,landscape}
--has-mobile-image

*/

.GridGallery__item--aspect-square {
  @apply w-1/6 aspect-square;
}

.GridGallery__item--aspect-landscape {
  aspect-ratio: 16/11;
  @apply w-1/4;
}

.GridGallery__item--has-mobile-image {
  .GridGallery__image--mobile {
    @apply block;
  }

  .GridGallery__image--desktop {
    @apply hidden;
  }

  @screen md {
    .GridGallery__image--mobile {
      @apply hidden;
    }

    .GridGallery__image--desktop {
      @apply block;
    }
  }
}
</style>
